import React from 'react';
import { Tag, Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Perk tags surfaced on a venue: an active discount and/or a gift. Both
// share the brand-success treatment and appear wherever a venue is shown
// as a card/row (LocationDetails hero, LocationCard, Home Tiers,
// StampDetailsModal). Rendering lives here so the label, icon, and colors
// stay consistent across all of them.
//
// `variant`:
//   'solid'  — filled green pill (image overlays: hero, card thumbnail)
//   'soft'   — translucent green (on dark surfaces: Home "Active now")
//   'inline' — text-only, no background (compact metadata rows: modal)

type Variant = 'solid' | 'soft' | 'inline';

const WRAP: Record<Variant, string> = {
  solid:
    'inline-flex items-center gap-1.5 bg-brand-success text-white font-heading font-bold text-xs px-3 py-1.5 rounded-lg shadow-sm',
  soft:
    'inline-flex items-center gap-1.5 bg-brand-success/30 text-brand-success font-heading font-bold text-xs px-3 py-1.5 rounded-lg whitespace-nowrap',
  inline:
    'inline-flex items-center gap-1.5 text-brand-success font-bold',
};

export const DiscountTag: React.FC<{
  percent: number;
  variant?: Variant;
}> = ({ percent, variant = 'solid' }) => {
  const { t } = useTranslation();
  return (
    <span className={WRAP[variant]}>
      <Tag className="w-3.5 h-3.5" />− {percent} % {t('ld_discount_active')}
    </span>
  );
};

export const GiftTag: React.FC<{ variant?: Variant }> = ({
  variant = 'solid',
}) => {
  const { t } = useTranslation();
  return (
    <span className={WRAP[variant]}>
      <Gift className="w-3.5 h-3.5" />
      {t('perk_gift')}
    </span>
  );
};

// Renders whichever perks a location has (discount, gift, or both),
// stacked in the same order everywhere. Returns null when there's nothing
// to show so callers can drop it in unconditionally.
const PerkTags: React.FC<{
  discountPercent?: number | null;
  hasGift?: boolean;
  variant?: Variant;
  className?: string;
}> = ({ discountPercent, hasGift, variant = 'solid', className = '' }) => {
  const showDiscount = !!discountPercent && discountPercent > 0;
  if (!showDiscount && !hasGift) return null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
      {showDiscount && (
        <DiscountTag percent={discountPercent as number} variant={variant} />
      )}
      {hasGift && <GiftTag variant={variant} />}
    </span>
  );
};

export default PerkTags;
